import { Button, ColorSwatches } from '@bennows/boows-react';
import { ArrowUpRight, Grid2x2, Info, MousePointer2, Pencil, Square, Trash2, Type, Undo2, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { boundsOf, drawMark, drawSelection, hitTest, MARK_COLORS, measuresOf, moveMark, type Mark, type Point, type Tool } from './feedback-draw';
import { useAnnotatorKeys } from './useAnnotatorKeys';

export const TOOLS: ReadonlyArray<{ readonly id: Tool; readonly key: string; readonly icon: LucideIcon }> = [
  { id: 'select', key: 'V', icon: MousePointer2 },
  { id: 'arrow', key: 'A', icon: ArrowUpRight },
  { id: 'box', key: 'R', icon: Square },
  { id: 'pen', key: 'P', icon: Pencil },
  { id: 'text', key: 'T', icon: Type },
  { id: 'blur', key: 'B', icon: Grid2x2 },
];
/** Screen pixels around a mark that still count as a hit. */
const HIT_SLOP_PX = 10;

type TextEntry = { readonly at: Point; readonly color: string; readonly left: number; readonly top: number };

interface Props {
  readonly image: HTMLCanvasElement;
  readonly marks: readonly Mark[];
  readonly onMarksChange: (marks: readonly Mark[]) => void;
  /** The drawn canvas the dialog exports; the selection frame is never part of it. */
  readonly canvasRef: RefObject<HTMLCanvasElement>;
  readonly busy: boolean;
}

/**
 * Marks on a screenshot (spec 05 section 6.1, style guide 2.101): arrow, box, pen, text, pixelate;
 * select to move or recolour, undo (Ctrl+Z), delete (Del), one-letter tool keys.
 */
export function FeedbackAnnotator({ image, marks, onMarksChange, canvasRef, busy }: Props) {
  const { t } = useTranslation();
  const [tool, setTool] = useState<Tool>('arrow');
  const [color, setColor] = useState<string>(MARK_COLORS[0].value);
  const [selected, setSelected] = useState(-1);
  const [draft, setDraft] = useState<Mark | null>(null);
  const [textEntry, setTextEntry] = useState<TextEntry | null>(null);
  const [text, setText] = useState('');
  const dragFrom = useRef<Point | null>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const measures = useMemo(() => measuresOf(image), [image]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);
    marks.forEach((mark) => drawMark(ctx, mark, image, measures));
    if (draft) drawMark(ctx, draft, image, measures);
    if (!busy && marks[selected]) drawSelection(ctx, marks[selected], measures);
  }, [canvasRef, image, marks, draft, selected, measures, busy]);

  const finishText = useCallback(
    (keep: boolean) => {
      if (!textEntry) return;
      const value = text.trim();
      setTextEntry(null);
      setText('');
      if (keep && value) onMarksChange([...marks, { kind: 'text', color: textEntry.color, at: textEntry.at, text: value }]);
    },
    [marks, onMarksChange, text, textEntry],
  );

  const chooseTool = useCallback(
    (next: Tool) => {
      finishText(true);
      setTool(next);
      if (next !== 'select') setSelected(-1);
    },
    [finishText],
  );
  const chooseColor = (next: string) => {
    setColor(next);
    const mark = marks[selected];
    if (mark && mark.kind !== 'blur') onMarksChange(marks.map((m, i) => (i === selected ? { ...m, color: next } : m)));
  };
  const undo = useCallback(() => {
    setSelected(-1);
    onMarksChange(marks.slice(0, -1));
  }, [marks, onMarksChange]);
  const removeSelected = useCallback(() => {
    if (selected < 0) return;
    onMarksChange(marks.filter((_, i) => i !== selected));
    setSelected(-1);
  }, [marks, onMarksChange, selected]);

  useAnnotatorKeys({ enabled: !busy && !textEntry, tools: TOOLS, onTool: chooseTool, onUndo: undo, onDelete: selected >= 0 ? removeSelected : null });

  // The text field sits where the person clicked; its position and colour go in as CSS variables.
  useEffect(() => {
    const field = textRef.current;
    if (!field || !textEntry) return;
    const canvas = canvasRef.current;
    const scale = canvas ? canvas.getBoundingClientRect().width / canvas.width : 1;
    field.style.setProperty('--feedback-text-left', `${textEntry.left}px`);
    field.style.setProperty('--feedback-text-top', `${textEntry.top}px`);
    field.style.setProperty('--feedback-text-color', textEntry.color);
    field.style.setProperty('--feedback-text-size', `${Math.max(13, measures.font * scale)}px`);
    field.focus();
  }, [canvasRef, measures.font, textEntry]);

  const pointOf = (event: ReactPointerEvent<HTMLCanvasElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: ((event.clientX - rect.left) * event.currentTarget.width) / rect.width, y: ((event.clientY - rect.top) * event.currentTarget.height) / rect.height };
  };
  const tolerance = (canvas: HTMLCanvasElement) => (HIT_SLOP_PX * canvas.width) / (canvas.getBoundingClientRect().width || canvas.width);

  const onPointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (busy) return;
    event.preventDefault();
    const canvas = event.currentTarget;
    const ctx = canvas.getContext('2d');
    const p = pointOf(event);
    if (tool === 'select') {
      const hit = ctx ? hitTest(ctx, marks, p, tolerance(canvas), measures) : -1;
      setSelected(hit);
      if (hit >= 0) {
        dragFrom.current = p;
        canvas.setPointerCapture?.(event.pointerId);
      }
      return;
    }
    if (tool === 'text') {
      if (textEntry) return finishText(true);
      const area = areaRef.current?.getBoundingClientRect();
      setTextEntry({ at: p, color, left: event.clientX - (area?.left ?? 0), top: event.clientY - (area?.top ?? 0) });
      return;
    }
    setDraft(tool === 'pen' ? { kind: 'pen', color, points: [p] } : { kind: tool, color, from: p, to: p });
    canvas.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const p = pointOf(event);
    if (dragFrom.current && marks[selected]) {
      const from = dragFrom.current;
      dragFrom.current = p;
      onMarksChange(marks.map((mark, i) => (i === selected ? moveMark(mark, p.x - from.x, p.y - from.y) : mark)));
      return;
    }
    if (!draft) return;
    setDraft(draft.kind === 'pen' ? { ...draft, points: [...draft.points, p] } : draft.kind === 'text' ? draft : { ...draft, to: p });
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (dragFrom.current) {
      dragFrom.current = null;
      return;
    }
    if (!draft) return;
    const ctx = event.currentTarget.getContext('2d');
    const bounds = ctx ? boundsOf(ctx, draft, measures) : { w: 0, h: 0 };
    // A stray click is not kept as a mark.
    if (Math.max(bounds.w, bounds.h) >= measures.line * 2) onMarksChange([...marks, draft]);
    setDraft(null);
  };

  return (
    <div className="ui-feedback-annotator">
      <div className="ui-feedback-annotator__toolbar" role="toolbar" aria-label={t('feedback.annotate.toolbar')}>
        <div className="ui-feedback-annotator__group" role="group" aria-label={t('feedback.annotate.tools')}>
          {TOOLS.map(({ id, key, icon: Icon }) => (
            <Button
              key={id}
              variant={tool === id ? 'secondary' : 'ghost'}
              size="sm"
              iconOnly
              aria-pressed={tool === id}
              aria-keyshortcuts={key}
              aria-label={t(`feedback.annotate.tool.${id}`)}
              title={t(`feedback.annotate.tool.${id}`)}
              onClick={() => chooseTool(id)}
            >
              <Icon aria-hidden="true" />
            </Button>
          ))}
        </div>
        <ColorSwatches
          className="ui-feedback-annotator__colors"
          label={t('feedback.annotate.colors')}
          hideLabel
          value={color}
          swatches={MARK_COLORS.map(({ key, value }) => ({ value, label: t(`feedback.annotate.color.${key}`) }))}
          onChange={chooseColor}
        />
        <div className="ui-feedback-annotator__group">
          <Button variant="ghost" size="sm" iconOnly aria-label={t('feedback.annotate.undo')} title={t('feedback.annotate.undo')} disabled={!marks.length} onClick={undo}>
            <Undo2 aria-hidden="true" />
          </Button>
          <Button variant="ghost" size="sm" iconOnly aria-label={t('feedback.annotate.delete')} title={t('feedback.annotate.delete')} disabled={selected < 0} onClick={removeSelected}>
            <Trash2 aria-hidden="true" />
          </Button>
        </div>
      </div>
      <div className="ui-feedback-annotator__area" ref={areaRef} data-tool={tool}>
        <canvas
          ref={canvasRef}
          className="ui-feedback-annotator__canvas"
          width={image.width}
          height={image.height}
          role="img"
          aria-label={t('feedback.annotate.canvas')}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
        {textEntry ? (
          <textarea
            ref={textRef}
            className={`ui-feedback-annotator__text${textEntry.color === MARK_COLORS[1].value ? ' is-light' : ''}`}
            rows={1}
            value={text}
            placeholder={t('feedback.annotate.textPlaceholder')}
            aria-label={t('feedback.annotate.textInput')}
            onChange={(event) => setText(event.target.value)}
            onBlur={() => finishText(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                finishText(true);
              } else if (event.key === 'Escape') {
                // preventDefault keeps the dialog open: Escape only drops this text.
                event.preventDefault();
                finishText(false);
              }
            }}
          />
        ) : null}
      </div>
      <p className="ui-feedback-annotator__tip">
        <Info aria-hidden="true" />
        <span>{t('feedback.annotate.tip')}</span>
      </p>
    </div>
  );
}
